import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  teal:   '#0d9488',
  tealBg: '#f0fdfa',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const ZAEHLERTYPEN = ['Strom', 'Gas', 'Wasser', 'Wärme', 'Kaltwasser', 'Warmwasser', 'Öl', 'Sonstige'];
const MASSEINHEITEN = ['kWh', 'm³', 'MWh', 'GJ', 'l', 'GWh'];
const VERSORGER = ['E.ON', 'RWE', 'EnBW', 'Vattenfall', 'Stadtwerke', 'Sonstige'];
const INTERVALLE = ['Monatlich', 'Vierteljährlich', 'Halbjährlich', 'Jährlich'];

const inputSt = {
  width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 8,
  padding: '10px 12px', fontSize: 13, boxSizing: 'border-box',
  fontFamily: T.font, color: T.ink, background: 'white', outline: 'none',
};
const selectSt = { ...inputSt, appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' };
const labelSt  = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: T.muted, marginBottom: 5 };

function SelectWrapper({ children }) {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <svg style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Export Modal
══════════════════════════════════════════════ */
function ExportModal({ zaehler, onClose }) {
  const { addToast } = useToast();
  const [format, setFormat]   = useState('csv');
  const [history, setHistory] = useState('current');

  function handleExport() {
    const header = ['Zählernummer', 'Typ', 'Maßeinheit', 'Name', 'Objekt', 'Status', 'Letzter Stand', 'Letztes Ablesen', 'Ableseintervall'];
    const rows = zaehler.map(z => [
      z.zaehlernummer, z.zaehlertyp, z.masseinheit, z.zaehler_name || '',
      z.property_name || '', z.status, z.letzter_stand || '', z.letztes_ablesen || '', z.ableseintervall,
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'zaehler.csv'; a.click();
    addToast('Zähler exportiert.', 'success');
    onClose();
  }

  const Radio = ({ val, group, current, onChange, label }) => (
    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:14, color:T.ink, padding:'4px 0' }}>
      <div onClick={() => onChange(val)} style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${current===val ? T.ink : T.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'border-color 0.15s' }}>
        {current === val && <div style={{ width:8, height:8, borderRadius:'50%', background:T.ink }} />}
      </div>
      {label}
    </label>
  );

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position:'fixed',inset:0,zIndex:200,background:'rgba(17,24,39,0.45)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div style={{ background:'white',borderRadius:16,width:'100%',maxWidth:480,boxShadow:'0 24px 64px rgba(0,0,0,0.16)' }}>
        <div style={{ padding:'22px 28px 18px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:`1px solid ${T.border}` }}>
          <h2 style={{ margin:0,fontSize:18,fontWeight:800,color:T.ink }}>Zähler exportieren</h2>
          <button onClick={onClose} style={{ width:32,height:32,borderRadius:'50%',border:`1.5px solid ${T.border}`,background:'white',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{ padding:'22px 28px 28px' }}>
          <p style={{ margin:'0 0 20px', fontSize:13, color:T.muted }}>{zaehler.length} Zähler ausgewählt</p>

          <div style={{ marginBottom:22 }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:10 }}>Format</div>
            <Radio val="csv"   current={format} onChange={setFormat} label="CSV" />
            <Radio val="excel" current={format} onChange={setFormat} label="Excel" />
          </div>

          <div style={{ marginBottom:28 }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:10 }}>Ablesehistorie</div>
            <Radio val="current"  current={history} onChange={setHistory} label="Nur aktuelle Zählerstände" />
            <Radio val="full"     current={history} onChange={setHistory} label="Mit vollständiger Historie" />
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={onClose} style={{ padding:'10px 20px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:14,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Abbrechen</button>
            <button onClick={handleExport} style={{ padding:'10px 24px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:T.font }}>Exportieren</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Create / Edit Modal
══════════════════════════════════════════════ */
function ZaehlerModal({ editing, properties, onSave, onClose }) {
  const EMPTY = { property_id:'', zaehlernummer:'', zaehlertyp:'', masseinheit:'', zaehler_name:'', lage:'', versorger:'', vertrags_nr:'', ableseintervall:'Jährlich', is_nebenzaehler:false, notiz:'' };
  const [form, setForm] = useState(editing ? {
    property_id:     editing.property_id || '',
    zaehlernummer:   editing.zaehlernummer || '',
    zaehlertyp:      editing.zaehlertyp || '',
    masseinheit:     editing.masseinheit || '',
    zaehler_name:    editing.zaehler_name || '',
    lage:            editing.lage || '',
    versorger:       editing.versorger || '',
    vertrags_nr:     editing.vertrags_nr || '',
    ableseintervall: editing.ableseintervall || 'Jährlich',
    is_nebenzaehler: editing.is_nebenzaehler || false,
    notiz:           editing.notiz || '',
  } : EMPTY);
  const [saving, setSaving] = useState(false);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  const ready = form.zaehlernummer.trim() && form.zaehlertyp && form.masseinheit;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!ready) return;
    setSaving(true);
    await onSave(form, editing?.id);
    setSaving(false);
  }

  const { isMobile } = useBreakpoint();
  const FRow = ({ children }) => <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14, marginBottom:14 }}>{children}</div>;
  const FCol = ({ label, children }) => <div><label style={labelSt}>{label}</label>{children}</div>;

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position:'fixed',inset:0,zIndex:200,background:'rgba(17,24,39,0.45)',backdropFilter:'blur(3px)',display:'flex',alignItems:'flex-start',justifyContent:'flex-end',padding:0 }}>
      <div style={{ background:'white',width:'100%',maxWidth:680,height:'100vh',overflowY:'auto',boxShadow:'-24px 0 64px rgba(0,0,0,0.16)',display:'flex',flexDirection:'column' }}>
        {/* Header */}
        <div style={{ padding:'24px 32px 20px',borderBottom:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0 }}>
          <h2 style={{ margin:0,fontSize:20,fontWeight:800,color:T.ink }}>{editing ? 'Zähler bearbeiten' : 'Zähler anlegen'}</h2>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',color:T.muted,padding:4,display:'flex' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ flex:1, padding:'28px 32px', overflowY:'auto' }}>
          {/* Section header */}
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,padding:'14px 18px',background:T.bg,borderRadius:10,border:`1px solid ${T.border}` }}>
            <span style={{ fontSize:15,fontWeight:800,color:T.ink }}>Allgemeine Informationen</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>
          </div>

          <FRow>
            <FCol label="Objekt / Einheit *">
              <SelectWrapper>
                <select value={form.property_id} onChange={e => set('property_id', e.target.value)} style={selectSt}
                  onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}>
                  <option value="">– kein Objekt –</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </SelectWrapper>
            </FCol>
            <FCol label="Zählernummer *">
              <input required value={form.zaehlernummer} onChange={e=>set('zaehlernummer',e.target.value)} style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
            </FCol>
          </FRow>

          <FRow>
            <FCol label="Zählertyp *">
              <SelectWrapper>
                <select required value={form.zaehlertyp} onChange={e=>set('zaehlertyp',e.target.value)} style={selectSt}
                  onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}>
                  <option value="">Bitte wählen</option>
                  {ZAEHLERTYPEN.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </SelectWrapper>
            </FCol>
            <FCol label="Maßeinheit *">
              <SelectWrapper>
                <select required value={form.masseinheit} onChange={e=>set('masseinheit',e.target.value)} style={selectSt}
                  onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}>
                  <option value="">Bitte wählen</option>
                  {MASSEINHEITEN.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </SelectWrapper>
            </FCol>
          </FRow>

          {/* Toggle */}
          <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:16 }}>
            <div onClick={() => set('is_nebenzaehler', !form.is_nebenzaehler)} style={{ width:42,height:24,borderRadius:12,background:form.is_nebenzaehler?T.ink:'#d1d5db',cursor:'pointer',position:'relative',transition:'background 0.2s',flexShrink:0 }}>
              <div style={{ position:'absolute',top:3,left:form.is_nebenzaehler?19:3,width:18,height:18,borderRadius:'50%',background:'white',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
            <span style={{ fontSize:13,color:T.ink,fontWeight:500 }}>Als Nebenzähler markieren</span>
          </div>

          <FRow>
            <FCol label="Zählername">
              <input value={form.zaehler_name} onChange={e=>set('zaehler_name',e.target.value)} placeholder="Zählername" style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
            </FCol>
            <FCol label="Lage des Zählers im Gebäude">
              <input value={form.lage} onChange={e=>set('lage',e.target.value)} placeholder="z.B. Keller, EG" style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
            </FCol>
          </FRow>

          <FRow>
            <FCol label="Versorger">
              <SelectWrapper>
                <select value={form.versorger} onChange={e=>set('versorger',e.target.value)} style={selectSt}
                  onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}>
                  <option value="">Bitte wählen</option>
                  {VERSORGER.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </SelectWrapper>
            </FCol>
            <FCol label="Vertrags-Nr.">
              <input value={form.vertrags_nr} onChange={e=>set('vertrags_nr',e.target.value)} style={inputSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border} />
            </FCol>
          </FRow>

          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Ableseintervall</label>
            <SelectWrapper>
              <select value={form.ableseintervall} onChange={e=>set('ableseintervall',e.target.value)} style={selectSt}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}>
                {INTERVALLE.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </SelectWrapper>
          </div>

          <div style={{ marginBottom:8 }}>
            <label style={labelSt}>Notiz</label>
            <textarea
              value={form.notiz} onChange={e => set('notiz', e.target.value.slice(0, 1000))}
              placeholder="Notiz"
              rows={5}
              style={{ ...inputSt, resize:'vertical', lineHeight:1.6 }}
              onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}
            />
            <div style={{ fontSize:11,color:T.muted,textAlign:'right',marginTop:4 }}>{form.notiz.length} / 1000</div>
          </div>
        </form>

        {/* Footer */}
        <div style={{ padding:'18px 32px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0 }}>
          <button onClick={onClose} style={{ padding:'10px 20px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:14,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Abbrechen</button>
          <button onClick={handleSubmit} disabled={!ready||saving} style={{ padding:'10px 28px',borderRadius:9,border:'none',background:ready?T.ink:'#d1d5db',color:'white',fontSize:14,fontWeight:700,cursor:ready?'pointer':'not-allowed',fontFamily:T.font }}>
            {saving ? 'Wird angelegt…' : editing ? 'Änderungen speichern' : 'Zähler anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Zaehler Page
══════════════════════════════════════════════ */
export default function Zaehler() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [zaehler, setZaehler]       = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing]       = useState(null);

  /* filters */
  const [search, setSearch]       = useState('');
  const [filterTyp, setFilterTyp] = useState('');
  const [filterProp, setFilterProp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterHauptNeben, setFilterHN] = useState('');

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const [{ data: z }, { data: p }] = await Promise.all([
      supabase.from('zaehler').select('*').eq('owner_id', user.id).order('created_at', { ascending: false }),
      supabase.from('properties').select('id,name').eq('owner_id', user.id).order('name'),
    ]);
    const propMap = Object.fromEntries((p || []).map(x => [x.id, x.name]));
    setZaehler((z || []).map(x => ({ ...x, property_name: propMap[x.property_id] || '' })));
    setProperties(p || []);
    setLoading(false);
  }

  async function handleSave(form, id) {
    const payload = { ...form, owner_id: user.id };
    const { error } = id
      ? await supabase.from('zaehler').update(payload).eq('id', id).eq('owner_id', user.id)
      : await supabase.from('zaehler').insert(payload);
    if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    await fetchAll();
    setShowCreate(false);
    setEditing(null);
    addToast(id ? 'Zähler aktualisiert.' : 'Zähler angelegt.', 'success');
  }

  async function handleDelete(id) {
    if (!window.confirm('Zähler wirklich löschen?')) return;
    await supabase.from('zaehler').delete().eq('id', id).eq('owner_id', user.id);
    setZaehler(z => z.filter(x => x.id !== id));
    addToast('Zähler gelöscht.', 'success');
  }

  const hasFilter = search || filterTyp || filterProp || filterStatus || filterHauptNeben;

  const filtered = zaehler.filter(z => {
    const q = search.toLowerCase();
    const matchSearch = !q || z.zaehlernummer?.toLowerCase().includes(q) || z.zaehler_name?.toLowerCase().includes(q) || z.property_name?.toLowerCase().includes(q);
    const matchTyp    = !filterTyp || z.zaehlertyp === filterTyp;
    const matchProp   = !filterProp || z.property_id === filterProp;
    const matchStatus = !filterStatus || z.status === filterStatus;
    const matchHN     = !filterHauptNeben || (filterHauptNeben === 'neben' ? z.is_nebenzaehler : !z.is_nebenzaehler);
    return matchSearch && matchTyp && matchProp && matchStatus && matchHN;
  });

  const Chip = ({ label, value, options, onChange }) => (
    <div style={{ position:'relative', display:'inline-flex', alignItems:'center' }}>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ appearance:'none', WebkitAppearance:'none', background:value?'#f5f5f5':'white', border:`1.5px solid ${value?T.ink:T.border}`, borderRadius:20, padding:'6px 28px 6px 12px', fontSize:12, color:value?T.ink:T.ink, cursor:'pointer', fontWeight:value?700:400, outline:'none', whiteSpace:'nowrap' }}>
        <option value="">{label}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg style={{ position:'absolute',right:8,pointerEvents:'none' }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
      {value && (
        <button onClick={() => onChange('')} style={{ position:'absolute',right:-8,top:-8,width:18,height:18,borderRadius:'50%',background:'#e5e5e5',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0 }}>
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth:1200, color:T.ink, fontFamily:T.font }}>

      {showExport && <ExportModal zaehler={filtered} onClose={() => setShowExport(false)} />}
      {(showCreate || editing) && (
        <ZaehlerModal
          editing={editing}
          properties={properties}
          onSave={handleSave}
          onClose={() => { setShowCreate(false); setEditing(null); }}
        />
      )}

      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28, gap:12, flexWrap:'wrap' }}>
        <h1 style={{ margin:0, fontSize: isMobile?22:28, fontWeight:800, letterSpacing:'-0.7px', color:T.ink }}>Zähler</h1>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => setShowExport(true)} style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:13,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Exportieren
          </button>
          <button onClick={() => setShowCreate(true)} style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:13,cursor:'pointer',fontFamily:T.font,fontWeight:700 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Neuen Zähler anlegen
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        {/* Search */}
        <div style={{ position:'relative', flex:'1 1 200px', minWidth:160 }}>
          <svg style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="text" placeholder="Zähler suchen" value={search} onChange={e=>setSearch(e.target.value)}
            style={{ width:'100%', border:`1.5px solid ${T.border}`, borderRadius:9, padding:'8px 12px 8px 32px', fontSize:13, boxSizing:'border-box', outline:'none', fontFamily:T.font, height:38 }} />
        </div>

        <button style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',border:`1.5px solid ${T.border}`,borderRadius:9,background:'white',fontSize:13,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600,height:38 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
          Filter
        </button>

        <Chip label="Zählertyp" value={filterTyp} onChange={setFilterTyp} options={ZAEHLERTYPEN.map(t=>({value:t,label:t}))} />
        <Chip label="Haupt-/Nebenzähler: Alle" value={filterHauptNeben} onChange={setFilterHN} options={[{value:'haupt',label:'Hauptzähler'},{value:'neben',label:'Nebenzähler'}]} />
        <Chip label="Alle Objekte" value={filterProp} onChange={setFilterProp} options={properties.map(p=>({value:p.id,label:p.name}))} />
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24, flexWrap:'wrap' }}>
        <Chip label="Status: Alle" value={filterStatus} onChange={setFilterStatus} options={[{value:'aktiv',label:'Aktiv'},{value:'inaktiv',label:'Inaktiv'}]} />
        {hasFilter && (
          <button onClick={() => { setSearch(''); setFilterTyp(''); setFilterProp(''); setFilterStatus(''); setFilterHN(''); }} style={{ background:'none',border:'none',fontSize:13,color:T.ink,cursor:'pointer',fontWeight:600,padding:'0 4px' }}>
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
          {[1,2,3].map(n => (
            <div key={n} style={{ padding:'16px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', gap:16, alignItems:'center' }}>
              <div style={{ flex:2 }}><div className="skeleton" style={{ width:'60%',height:13,borderRadius:6,marginBottom:6 }}/><div className="skeleton" style={{ width:'40%',height:11,borderRadius:5 }}/></div>
              <div className="skeleton" style={{ flex:1, height:13, borderRadius:5 }}/>
              <div className="skeleton" style={{ flex:1, height:13, borderRadius:5 }}/>
              <div className="skeleton" style={{ width:60, height:20, borderRadius:100 }}/>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
          {hasFilter ? (
            <div style={{ textAlign:'center', padding:'60px 24px' }}>
              <div style={{ width:56,height:56,background:'#f7f7f7',borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
              <div style={{ fontSize:16,fontWeight:700,color:T.ink,marginBottom:8 }}>Keine Zähler gefunden</div>
              <div style={{ fontSize:13,color:T.muted,lineHeight:1.7,marginBottom:20 }}>Versuche andere Filter oder setze die Suche zurück.</div>
              <button onClick={() => { setSearch(''); setFilterTyp(''); setFilterProp(''); setFilterStatus(''); setFilterHN(''); }} className="btn-secondary">Filter zurücksetzen</button>
            </div>
          ) : (
            <div style={{ padding:'48px 40px' }}>
              <div style={{ display:'flex',alignItems:'flex-start',gap:32,marginBottom:40 }}>
                <div style={{ flexShrink:0,width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',border:`1.5px solid ${T.border}` }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l2.5 2.5"/><path d="M16.5 5.5l1-1"/><path d="M7.5 5.5l-1-1"/></svg>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:20,fontWeight:700,color:T.ink,marginBottom:8,letterSpacing:'-0.3px' }}>Noch keine Zähler vorhanden</div>
                  <div style={{ fontSize:14,color:T.muted,lineHeight:1.8,maxWidth:520 }}>Erfasse Strom-, Gas-, Wasser- und Wärmezähler für deine Objekte. Zählerstände bilden die Grundlage für die Nebenkostenabrechnung.</div>
                  <button onClick={() => setShowCreate(true)} className="btn-primary" style={{ marginTop:20 }}>+ Ersten Zähler anlegen</button>
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',gap:16 }}>
                {[
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>, title:'Verbrauch erfassen', text:'Trage Zählerstände manuell ein oder importiere sie.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>, title:'Nebenkosten', text:'Verbräuche fließen direkt in die Nebenkostenabrechnung ein.' },
                  { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h18v18H3z"/><path d="M3 9h18M9 21V9"/></svg>, title:'Mehrere Typen', text:'Strom, Gas, Wasser, Wärme — alle Zählertypen an einem Ort.' },
                ].map((f,i)=>(
                  <div key={i} style={{ background:'#f7f7f7',borderRadius:12,padding:'18px 20px',border:`1px solid ${T.border}` }}>
                    <div style={{ width:36,height:36,background:'white',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12,border:`1px solid ${T.border}`,boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                    <div style={{ fontSize:13,fontWeight:700,color:T.ink,marginBottom:4 }}>{f.title}</div>
                    <div style={{ fontSize:12,color:T.muted,lineHeight:1.6 }}>{f.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Table / Cards */
        isMobile ? (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(z => (
              <div key={z.id} style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:T.ink }}>{z.zaehlernummer}</div>
                    {z.zaehler_name && <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>{z.zaehler_name}</div>}
                    {z.is_nebenzaehler && <span style={{ fontSize:10,background:'#fef3c7',color:'#92400e',padding:'2px 7px',borderRadius:10,fontWeight:700,marginTop:3,display:'inline-block' }}>Nebenzähler</span>}
                  </div>
                  <span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:z.status==='aktiv'?'#dcfce7':'#f3f4f6',color:z.status==='aktiv'?'#15803d':'#888',flexShrink:0 }}>
                    {z.status === 'aktiv' ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
                <div style={{ fontSize:12, color:T.muted, marginBottom:4 }}>{z.zaehlertyp} ({z.masseinheit})</div>
                {z.property_name && <div style={{ fontSize:12, color:T.muted, marginBottom:4 }}>{z.property_name}</div>}
                <div style={{ fontSize:13, fontWeight:600, color:T.ink, marginBottom:12 }}>
                  {z.letzter_stand != null ? `${z.letzter_stand} ${z.masseinheit}` : 'Kein Stand'}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => setEditing(z)} style={{ flex:1, background:'none',border:`1px solid ${T.border}`,borderRadius:8,padding:'8px 0',fontSize:12,cursor:'pointer',color:T.ink,fontFamily:T.font,fontWeight:600 }}>Bearbeiten</button>
                  <button onClick={() => handleDelete(z.id)} style={{ background:'none',border:`1px solid #fecaca`,borderRadius:8,padding:'8px 12px',fontSize:12,cursor:'pointer',color:'#dc2626',fontFamily:T.font }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          {/* Header row */}
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 100px', padding:'11px 20px', borderBottom:`1px solid ${T.border}`, background:T.bg }}>
            {['Zähler', 'Typ', 'Objekt', 'Letzter Stand', 'Status', ''].map(h => (
              <span key={h} style={{ fontSize:11, fontWeight:700, letterSpacing:'0.5px', textTransform:'uppercase', color:'#9ca3af' }}>{h}</span>
            ))}
          </div>
          {filtered.map((z, i) => (
            <div key={z.id} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 100px', padding:'14px 20px', alignItems:'center', borderBottom: i < filtered.length-1 ? `1px solid ${T.border}` : 'none', transition:'background 0.1s' }}
              onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div>
                <div style={{ fontSize:13,fontWeight:700,color:T.ink }}>{z.zaehlernummer}</div>
                {z.zaehler_name && <div style={{ fontSize:11,color:T.muted,marginTop:2 }}>{z.zaehler_name}</div>}
                {z.is_nebenzaehler && <span style={{ fontSize:10,background:'#fef3c7',color:'#92400e',padding:'2px 7px',borderRadius:10,fontWeight:700,marginTop:3,display:'inline-block' }}>Nebenzähler</span>}
              </div>
              <div style={{ fontSize:13,color:T.ink }}>{z.zaehlertyp} <span style={{ color:T.muted,fontSize:11 }}>({z.masseinheit})</span></div>
              <div style={{ fontSize:12,color:T.muted }}>{z.property_name || '–'}</div>
              <div style={{ fontSize:13,color:T.ink }}>{z.letzter_stand != null ? `${z.letzter_stand} ${z.masseinheit}` : '–'}</div>
              <div>
                <span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:z.status==='aktiv'?'#dcfce7':'#f3f4f6',color:z.status==='aktiv'?'#15803d':'#888' }}>
                  {z.status === 'aktiv' ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
              <div style={{ display:'flex',gap:6,justifyContent:'flex-end' }}>
                <button onClick={() => setEditing(z)} style={{ background:'none',border:`1px solid ${T.border}`,borderRadius:7,padding:'5px 10px',fontSize:11,cursor:'pointer',color:T.ink,fontFamily:T.font,fontWeight:600 }}>Bearb.</button>
                <button onClick={() => handleDelete(z.id)} style={{ background:'none',border:`1px solid #fecaca`,borderRadius:7,padding:'5px 8px',fontSize:11,cursor:'pointer',color:'#dc2626',fontFamily:T.font }}>✕</button>
              </div>
            </div>
          ))}
        </div>
        )
      )}
    </div>
  );
}
